import { BaseClient } from './baseClient';
import { GetRFQsResponse, RFQEndUser } from '../types/rfq.types';
import { APIResponse, OrgMemberOption, RFQ, RFQBuyerGstin, RFQAccessList, RFQCollaborator, RFQRole } from '../types';
import { QuoteRecommendationResult, QuoteRecommendationRun, QuoteRecommendationSnapshot } from '../types/quote.types';
import { CriteriaPointersResult } from '../types/criteriaPointers';
import { CriteriaChanges } from '../types/robbieProposal';

type RecommendationEnvelope = APIResponse<QuoteRecommendationResult | null> & { run?: QuoteRecommendationRun | null };

class TrpClient extends BaseClient {
    constructor() {
        super();
    }

    async getRFQs(page = 1, limit = 20, scope?: 'org' | 'mine'): Promise<GetRFQsResponse> {
        // Server default is org-wide (matches the org-scoped dashboard
        // summary); pass scope only to narrow to the caller's own RFQs.
        const response = await this.api.get<GetRFQsResponse>('/trp/rfqs', {
            params: scope ? { page, limit, scope } : { page, limit }
        });
        return response.data;
    }

    async createRFQ(name?: string, endUser?: RFQEndUser): Promise<RFQ> {
        const payload: Record<string, unknown> = {};
        if (name) payload.name = name;
        if (endUser?.email) payload.endUser = endUser;
        const response = await this.api.post<APIResponse<RFQ>>('/trp/rfqs', payload);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to create RFQ');
    }

    /**
     * Open (or reopen) the end user's write window on the technical sub-steps.
     * The invite email is a backend placeholder for now (`emailSent: false`).
     * Optionally names/renames the end user in the same call.
     */
    async shareWithEndUser(rfqId: string, endUser?: RFQEndUser): Promise<RFQ> {
        const response = await this.api.post<APIResponse<RFQ>>(
            `/trp/rfqs/${rfqId}/end-user/share`,
            endUser ?? {}
        );
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to share with the end user');
    }

    /** End-user sign-off: closes their write window and hands the RFQ back. */
    async signOffAsEndUser(rfqId: string): Promise<RFQ> {
        const response = await this.api.post<APIResponse<RFQ>>(`/trp/rfqs/${rfqId}/end-user/signoff`, {});
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to sign off');
    }

    async getRFQ(id: string): Promise<RFQ> {
        const response = await this.api.get<APIResponse<RFQ>>(`/trp/rfqs/${id}`);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to fetch RFQ');
    }

    async uploadDocuments(
        rfqId: string,
        files: File[],
        onProgress?: (percent: number) => void
    ): Promise<any[]> {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file);
        });

        const response = await this.api.post<APIResponse<any[]>>(`/trp/rfqs/${rfqId}/documents`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (event) => {
                if (onProgress && event.total) {
                    onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
                }
            },
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to upload documents');
    }

    async getDocuments(rfqId: string): Promise<any[]> {
        const response = await this.api.get<APIResponse<any[]>>(`/trp/rfqs/${rfqId}/documents`);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to fetch documents');
    }

    async deleteDocument(documentId: string): Promise<boolean> {
        const response = await this.api.delete<APIResponse<any>>(`/trp/documents/${documentId}`);
        return response.data.success || false;
    }

    async analyzeBOM(rfqId: string, documentIds: string[]): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(`/trp/rfqs/${rfqId}/bom/analyze`, {
            documentIds
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to analyze BOM');
    }

    async uploadDatasheets(files: File[], onProgress?: (percent: number) => void): Promise<any> {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file);
        });

        const response = await this.api.post<APIResponse<any>>('/trp/documents/upload-datasheet', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (event) => {
                if (onProgress && event.total) {
                    onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
                }
            },
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to upload datasheets');
    }

    /** Changes only the named criteria; the workflow stays where it is (a Robbie card's apply). */
    async patchRequirements(id: string, changes: CriteriaChanges): Promise<Record<string, unknown>> {
        const response = await this.api.patch<APIResponse<{ requirements: Record<string, unknown> }>>(
            `/trp/rfqs/${id}/requirements`,
            { changes }
        );
        if (response.data.success && response.data.data) return response.data.data.requirements;
        throw new Error(response.data.error || 'Failed to update the sourcing criteria');
    }

    async updateRequirements(
        id: string,
        requirements: {
            tradeoffPriorities: string[];
            complianceRequirements: string[];
            desiredLeadTime: string;
            deliveryLocation: {
                formattedAddress: string;
                latitude?: number;
                longitude?: number;
            };
            sourcingLocation?: string;
            notes?: string;
        }
    ): Promise<{ rfq: any }> {
        const response = await this.api.put<APIResponse<{ rfq: any }>>(`/trp/rfqs/${id}/requirements`, {
            requirements,
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to update requirements');
    }

    /** The server reads the notes on first call (a few seconds), then serves its cache. */
    async getCriteriaPointers(id: string): Promise<CriteriaPointersResult> {
        const response = await this.api.get<APIResponse<CriteriaPointersResult>>(`/trp/rfqs/${id}/criteria-pointers`);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to fetch criteria pointers');
    }

    async updateRFQ(id: string, data: Partial<RFQ>): Promise<RFQ> {
        const response = await this.api.patch<APIResponse<RFQ>>(`/trp/rfqs/${id}`, data);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to update RFQ');
    }

    /** Saves the buyer's GSTIN on the RFQ (and on their company's site). `city` only when the server asks for it. */
    async setBuyerGstin(id: string, gstin: string, city?: string): Promise<RFQBuyerGstin> {
        const response = await this.api.put<APIResponse<{ buyerGstin: RFQBuyerGstin }>>(
            `/trp/rfqs/${id}/buyer-gstin`, city ? { gstin, city } : { gstin }
        );
        if (response.data.success && response.data.data?.buyerGstin) return response.data.data.buyerGstin;
        throw new Error(response.data.error || 'Failed to save the GSTIN');
    }

    // --- SHARING ---
    // The server owns every rule here (owner-only management, org membership,
    // no duplicates) and every refusal arrives already written in Robbie's
    // voice, so these stay thin and let the message through untouched.

    async getRfqAccess(id: string): Promise<RFQAccessList> {
        const response = await this.api.get<APIResponse<RFQAccessList>>(`/trp/rfqs/${id}/collaborators`);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to load who has access');
    }

    async getRfqCollaboratorCandidates(id: string): Promise<OrgMemberOption[]> {
        const response = await this.api.get<APIResponse<OrgMemberOption[]>>(
            `/trp/rfqs/${id}/collaborator-candidates`
        );
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to load your team');
    }

    async addRfqCollaborator(id: string, userId: string, role: RFQRole): Promise<RFQCollaborator> {
        const response = await this.api.post<APIResponse<RFQCollaborator>>(
            `/trp/rfqs/${id}/collaborators`,
            { userId, role }
        );
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to share the RFQ');
    }

    // 204 with no body — there is nothing to unwrap, and a thrown axios error
    // is the only failure signal.
    async removeRfqCollaborator(id: string, userId: string): Promise<void> {
        await this.api.delete(`/trp/rfqs/${id}/collaborators/${encodeURIComponent(userId)}`);
    }

    // --- WORKFLOW ---

    async completeStep(id: string, step: number): Promise<any> {
        const response = await this.api.put<APIResponse<any>>(`/trp/rfqs/${id}/step/${step}`, { step });
        if (response.data.success) {
            return response.data.data;
        }
        throw new Error(response.data.error || `Failed to complete step ${step}`);
    }

    // --- QUOTES & PROPOSALS ---

    async getQuoteRecommendation(id: string): Promise<QuoteRecommendationSnapshot> {
        const response = await this.api.get<RecommendationEnvelope>(`/trp/rfqs/${id}/quotes/recommendation`);
        if (response.data.success) {
            return { data: response.data.data || null, run: response.data.run || null };
        }
        throw new Error(response.data.error || 'Failed to fetch quote recommendation');
    }

    /** 201 carries the fresh recommendation; 202 means a run is already going — poll the GET. */
    async generateQuoteRecommendation(id: string): Promise<QuoteRecommendationSnapshot> {
        const response = await this.api.post<RecommendationEnvelope>(`/trp/rfqs/${id}/quotes/recommendation`);
        if (response.data.success) {
            return { data: response.data.data || null, run: response.data.run || null };
        }
        throw new Error(response.data.error || 'Failed to generate quote recommendation');
    }

    async acceptQuoteRecommendation(
        id: string,
        planComponents?: Array<{ componentId: string; allocations: Array<{ supplierName: string; sharePercent: number }> }>
    ): Promise<QuoteRecommendationResult> {
        const response = await this.api.post<APIResponse<QuoteRecommendationResult>>(
            `/trp/rfqs/${id}/quotes/accept`,
            planComponents ? { components: planComponents } : {}
        );
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to accept quote recommendation');
    }

    // --- FINAL DELIVERABLES ---

    async downloadSourcingSummary(id: string): Promise<Blob> {
        const response = await this.api.get(`/trp/rfqs/${id}/export/sourcing-summary`, { responseType: 'blob' });
        return response.data;
    }

    /** Slug comes from supplierSlug() over the awarded supplier's name. */
    async downloadSupplierRfq(id: string, supplierSlug: string): Promise<Blob> {
        const response = await this.api.get(
            `/trp/rfqs/${id}/export/supplier-rfqs/${supplierSlug}`,
            { responseType: 'blob' },
        );
        return response.data;
    }

    async downloadSmartBom(id: string): Promise<Blob> {
        const response = await this.api.get(`/trp/rfqs/${id}/export/smart-bom`, { responseType: 'blob' });
        return response.data;
    }
}

export const trpClient = new TrpClient();
